import * as THREE from "three";

export default function(
  x,
  y,
  z,
  cardWidth,
  cardHeight,
  size,
  id,
  text,
  textColor,
  path,
  front,
  back,
  type,
  number,
  camera,
  textureLoader
) {
  /*
   * @function - Image
   * Creates image element to add to card
   * @param - materialEmpty (ThreeJS Material Instance): Empty material for faces with no texture
   */
  function Image(materialEmpty) {
    Object.setPrototypeOf(this, new THREE.Object3D());

    const texture = textureLoader.load(path);
    const materialSurface = new THREE.MeshPhongMaterial({ map: texture });
    const material = [
      materialEmpty,
      materialEmpty,
      materialEmpty,
      materialEmpty,
      materialEmpty,
      materialSurface
    ];

    const geometry = new THREE.BoxBufferGeometry(cardWidth * 0.95, cardHeight * 0.6, 0.13);
    const mesh = new THREE.Mesh(geometry, material);

    this.add(mesh);
  }

  /*
   * @function - Text
   * Creates text element to add to card
   * @param - materialEmpty (ThreeJS Material Instance): Empty material for faces with no texture
   */
  function Text(materialEmpty) {
    Object.setPrototypeOf(this, new THREE.Object3D());

    let enter = 20;
    const charLength = 30;
    const textDivide = [];
    const canvas = document.createElement("canvas");
    const canvasContext = canvas.getContext("2d");
    canvas.width = 180;
    canvas.height = 180;
    canvasContext.lineWidth = 1;
    canvasContext.shadowColor = "#33ff33";
    canvasContext.shadowBlur = 40;
    canvasContext.fillStyle = textColor;
    canvasContext.font = " bold 8pt arial";

    for (let i = 0; i < text.length; i += charLength) {
      if (text.length - charLength > i) {
        textDivide.push(text.substring(i, i + charLength));
      } else {
        textDivide.push(text.substring(i, text.length));
      }
    }

    for (let i = 0; i < textDivide.length; i++) {
      if (
        i < textDivide.length - 1 &&
        textDivide[i][textDivide[i].length - 1] !== " " &&
        textDivide[i + 1][0] !== " "
      ) {
        canvasContext.fillText(`${textDivide[i]}-`, 5, enter);
      } else {
        canvasContext.fillText(textDivide[i], 5, enter);
      }

      enter += 10;
    }

    const texture = new THREE.Texture(canvas);

    const materialSurface = new THREE.MeshLambertMaterial({ map: texture, transparent: true });
    materialSurface.map.needsUpdate = true;

    const material = [
      materialEmpty,
      materialEmpty,
      materialEmpty,
      materialEmpty,
      materialEmpty,
      materialSurface
    ];

    const geometry = new THREE.BoxBufferGeometry(cardWidth, cardHeight, 0.125);
    const mesh = new THREE.Mesh(geometry, material);

    this.add(mesh);
  }

  const textureFront = textureLoader.load(front);
  const textureBack = textureLoader.load(back);
  const textureEmpty = new THREE.Texture(document.createElement("canvas")); // Empty canvas

  const materialFront = new THREE.MeshPhongMaterial({ map: textureFront });
  const materialBack = new THREE.MeshPhongMaterial({ map: textureBack });
  const materialEmpty = new THREE.MeshLambertMaterial({ map: textureEmpty, transparent: true });
  materialEmpty.map.needsUpdate = true;

  const geometry = new THREE.BoxBufferGeometry(cardWidth, cardHeight, 0.0125);
  const material = [
    materialBack,
    materialBack,
    materialBack,
    materialBack,
    materialBack,
    materialFront
  ];

  Object.setPrototypeOf(this, new THREE.Mesh(geometry, material));
  this.position.set(x, y, z);
  this.name = "Card";
  this.number = number;
  this.type = type;
  this.cardInMovement = false;
  this.realX = x;
  this.realY = y;
  this.realZ = z;
  this.angleY = this.rotation.y;
  this.idValue = id;

  if (text) {
    this.add(new Text(materialEmpty));
  }

  if (path) {
    const image = new Image(materialEmpty);
    if (text) {
      image.position.y = -(cardHeight / 2) + cardHeight * 0.32;
    }

    this.add(image);
  }

  const velocityRatio = 100;

  /*
   * @function - perform
   * Action of agents oriented programming 3d interfaces
   * @param - scene (ThreeJS Scene Instance): Scene instance of the 3d scene
   */
  function perform() {
    if (this.name === "Card") {
      const fixedX = this.position.x.toFixed(2);
      const fixedY = this.position.y.toFixed(2);
      const fixedZ = this.position.z.toFixed(2);

      if (this.cardInMovement === true) {
        if (fixedZ <= size / 2) {
          this.position.z += size / velocityRatio;
          if (Math.abs(fixedY) > (size / velocityRatio) * 2) {
            if (this.realY > 0 && Math.abs(fixedY) > 0) {
              this.position.y -= size / velocityRatio;
            } else if (this.realY < 0 && Math.abs(fixedY) > 0) {
              this.position.y += size / velocityRatio;
            }
          }
          if (Math.abs(fixedX) > (size / velocityRatio) * 2) {
            if (this.realX > camera.position.x && fixedX > camera.position.x) {
              this.position.x -= size / velocityRatio;
            } else if (this.realX < camera.position.x && fixedX < camera.position.x) {
              this.position.x += size / velocityRatio;
            }
          }
        }
        if (this.rotation.y < this.angleY + Math.PI) {
          this.rotation.y += size / (2 * velocityRatio);
        } else if (this.rotation.y !== this.angleY + Math.PI) {
          this.rotation.y = this.angleY + Math.PI;
        }
      } else {
        if (fixedZ > this.realZ) {
          this.position.z -= size / velocityRatio;
        }

        if (this.realY > fixedY) {
          this.position.y += size / velocityRatio;
        } else if (this.realY < fixedY) {
          this.position.y -= size / velocityRatio;
        }

        if (this.realX > fixedX) {
          this.position.x += size / velocityRatio;
        } else if (this.realX < fixedX) {
          this.position.x -= size / velocityRatio;
        }

        if (this.angleY < this.rotation.y) {
          this.rotation.y -= size / (2 * velocityRatio);
        } else {
          this.rotation.y = 0;
        }
      }
    } else if (this.rotation.y < this.angleY + Math.PI) {
      this.rotation.y += size / (2 * velocityRatio);
    } else if (this.rotation.y !== this.angleY + Math.PI) {
      this.rotation.y = this.angleY + Math.PI;
    }
  }

  this.perform = perform;
}
